// FILE: src/components/DataToolsPanel.js
//
// Admin → Data Tools panel.  Three action cards plus one disabled v2
// placeholder.  Runs behind the AdminGate so only signed-in admins
// can reach it.
//
// Actions:
//   1. Clear Test Sessions       — calls admin_clear_test_sessions RPC.
//                                  Simple confirm dialog.
//   2. Load Test Data            — generates 5 synthetic questions and
//                                  hands them to the parent's onImport
//                                  callback (the same pipeline Bulk
//                                  Import uses).
//   3. Clear All User Sessions   — nuclear.  Gated behind a
//                                  type-the-word-DELETE text input.
//   4. Refresh from Prod         — disabled placeholder for v2.
//
// The panel is intentionally styleless-beyond-inline-CSS to stay
// consistent with the rest of the admin UI.  No new dependencies.

import React, { useState } from 'react';
import {
  clearTestSessions,
  clearAllUserSessions,
  generateTestQuestions,
} from '../../services/dataToolsService';
import { getAllExams } from '../../config/exams';

const styles = {
  wrapper: {
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    minHeight: '100vh',
    background: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: '#1a1a2e',
    color: '#fff',
    padding: '16px 28px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
  },
  backButton: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #555',
    borderRadius: '4px',
    color: '#ccc',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  headerTitle: {
    fontSize: '1.15rem',
    fontWeight: 'bold',
    flex: 1,
    letterSpacing: '0.5px',
  },
  body: {
    flex: 1,
    padding: '40px 32px',
    maxWidth: '900px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '8px',
    marginTop: 0,
  },
  sectionIntro: {
    fontSize: '0.9rem',
    color: '#666',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  card: (enabled) => ({
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    padding: '20px 24px',
    marginBottom: '16px',
    opacity: enabled ? 1 : 0.55,
  }),
  cardTitle: {
    fontSize: '1.05rem',
    fontWeight: 'bold',
    color: '#222',
    marginBottom: '6px',
  },
  cardDescription: {
    fontSize: '0.9rem',
    color: '#555',
    lineHeight: '1.5',
    marginBottom: '14px',
  },
  cardRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  primaryButton: {
    padding: '8px 16px',
    background: '#1976d2',
    border: 'none',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  dangerButton: {
    padding: '8px 16px',
    background: '#c62828',
    border: 'none',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  disabledButton: {
    padding: '8px 16px',
    background: '#ccc',
    border: 'none',
    borderRadius: '6px',
    color: '#666',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'not-allowed',
  },
  select: {
    padding: '6px 10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  toast: (variant) => ({
    marginTop: '14px',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '0.85rem',
    background: variant === 'error' ? '#ffebee' : '#e8f5e9',
    color:      variant === 'error' ? '#c62828' : '#2e7d32',
    border: variant === 'error' ? '1px solid #ef9a9a' : '1px solid #a5d6a7',
  }),
  badge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '0.72rem',
    fontWeight: '700',
    background: '#f0f0f0',
    color: '#999',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    marginLeft: '10px',
  },

  // ── Confirmation modal ──────────────────────────────────────────────
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    border: '2px solid #333',
    borderRadius: '8px',
    padding: '28px',
    maxWidth: '440px',
    width: '92%',
  },
  modalHeading: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#c62828',
    marginTop: 0,
    marginBottom: '10px',
  },
  modalBody: {
    fontSize: '0.95rem',
    color: '#444',
    lineHeight: '1.5',
    marginBottom: '18px',
  },
  modalInput: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.95rem',
    marginBottom: '18px',
    boxSizing: 'border-box',
  },
  modalActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
  },
  modalCancel: {
    padding: '8px 18px',
    background: 'transparent',
    border: '1px solid #999',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: '#555',
  },
};

export default function DataToolsPanel({ onBack, onImport }) {
  // ── Generic action status ─────────────────────────────────────────
  // Each action card manages its own toast so success/error messages
  // appear next to the button that triggered them.
  const [testClearStatus,   setTestClearStatus]   = useState(null);
  const [loadStatus,        setLoadStatus]        = useState(null);
  const [userClearStatus,   setUserClearStatus]   = useState(null);
  const [busy,              setBusy]              = useState(null); // action id

  // ── Load Test Data state ──────────────────────────────────────────
  const [loadExamId, setLoadExamId] = useState('pmp');

  // ── Nuclear confirmation dialog state ─────────────────────────────
  const [showNukeModal, setShowNukeModal] = useState(false);
  const [nukeConfirmText, setNukeConfirmText] = useState('');

  // ── Action handlers ───────────────────────────────────────────────

  async function handleClearTest() {
    if (!window.confirm('Delete all exam_sessions rows tagged source=\'test\'?')) return;
    setBusy('clearTest');
    setTestClearStatus(null);
    try {
      const n = await clearTestSessions();
      setTestClearStatus({ variant: 'ok', message: `Deleted ${n} test session${n === 1 ? '' : 's'}.` });
    } catch (err) {
      setTestClearStatus({ variant: 'error', message: err.message });
    } finally {
      setBusy(null);
    }
  }

  async function handleLoadTestData() {
    if (!onImport) {
      setLoadStatus({ variant: 'error', message: 'Import pipeline not available.' });
      return;
    }
    setBusy('loadData');
    setLoadStatus(null);
    try {
      const qs = generateTestQuestions(loadExamId, 5);
      const result = await onImport(loadExamId, qs);
      const saved  = result?.saved ?? qs.length;
      const errors = result?.errors ?? [];
      if (errors.length > 0) {
        setLoadStatus({
          variant: 'error',
          message: `Saved ${saved}/${qs.length} synthetic questions. Failures: ${errors.join('; ')}`,
        });
      } else {
        setLoadStatus({
          variant: 'ok',
          message: `Added ${saved} synthetic ${loadExamId.toUpperCase()} questions (status: new, review: unreviewed).`,
        });
      }
    } catch (err) {
      setLoadStatus({ variant: 'error', message: err.message });
    } finally {
      setBusy(null);
    }
  }

  async function handleConfirmNuke() {
    if (nukeConfirmText !== 'DELETE') return;
    setBusy('clearUsers');
    setUserClearStatus(null);
    setShowNukeModal(false);
    try {
      const n = await clearAllUserSessions();
      setUserClearStatus({ variant: 'ok', message: `Deleted ${n} user session${n === 1 ? '' : 's'}.` });
    } catch (err) {
      setUserClearStatus({ variant: 'error', message: err.message });
    } finally {
      setBusy(null);
      setNukeConfirmText('');
    }
  }

  function openNukeModal() {
    setNukeConfirmText('');
    setShowNukeModal(true);
  }

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div data-testid="data-tools-panel" style={styles.wrapper}>
      <div style={styles.header}>
        <button
          data-testid="data-tools-back-button"
          style={styles.backButton}
          onClick={onBack}
        >
          ← Back
        </button>
        <span style={styles.headerTitle}>🛠️ Data Tools</span>
      </div>

      <div style={styles.body}>
        <p style={styles.sectionTitle}>Maintenance</p>
        <p style={styles.sectionIntro}>
          Utilities for resetting session history and seeding the current
          environment with throwaway data for feature validation. All
          actions apply to the Supabase project this build is connected to.
        </p>

        {/* ── Clear Test Sessions ─────────────────────────────────────── */}
        <div style={styles.card(true)}>
          <div style={styles.cardTitle}>Clear Test Sessions</div>
          <div style={styles.cardDescription}>
            Deletes every <code>exam_sessions</code> row tagged{' '}
            <code>source='test'</code>. These rows come from the CI Playwright
            e2e runs and inflate dashboard counts if left in place.
          </div>
          <div style={styles.cardRow}>
            <button
              data-testid="data-tools-clear-test-button"
              style={styles.primaryButton}
              disabled={busy !== null}
              onClick={handleClearTest}
            >
              {busy === 'clearTest' ? 'Clearing…' : 'Clear Test Sessions'}
            </button>
          </div>
          {testClearStatus && (
            <div style={styles.toast(testClearStatus.variant)}>
              {testClearStatus.message}
            </div>
          )}
        </div>

        {/* ── Load Test Data ──────────────────────────────────────────── */}
        <div style={styles.card(true)}>
          <div style={styles.cardTitle}>Load Test Data (5 synthetic questions)</div>
          <div style={styles.cardDescription}>
            Generates a batch of 5 obviously-fake questions and imports them
            with <code>status=new</code> and <code>review_status=unreviewed</code>{' '}
            so they appear in the review queue. Useful for validating new
            features without waiting for real content.
          </div>
          <div style={styles.cardRow}>
            <label htmlFor="data-tools-exam-select" style={{ fontSize: '0.9rem', color: '#444' }}>
              Exam:
            </label>
            <select
              id="data-tools-exam-select"
              data-testid="data-tools-exam-select"
              style={styles.select}
              value={loadExamId}
              onChange={(e) => setLoadExamId(e.target.value)}
              disabled={busy !== null}
            >
              {/* Driven from getAllExams() so new certifications added in
                  src/config/exams.js show up automatically. */}
              {getAllExams().map((exam) => (
                <option key={exam.examId} value={exam.examId}>
                  {exam.displayName}
                </option>
              ))}
            </select>
            <button
              data-testid="data-tools-load-data-button"
              style={styles.primaryButton}
              disabled={busy !== null}
              onClick={handleLoadTestData}
            >
              {busy === 'loadData' ? 'Loading…' : 'Load 5 Test Questions'}
            </button>
          </div>
          {loadStatus && (
            <div style={styles.toast(loadStatus.variant)}>
              {loadStatus.message}
            </div>
          )}
        </div>

        {/* ── Clear All User Sessions (NUCLEAR) ───────────────────────── */}
        <div style={styles.card(true)}>
          <div style={styles.cardTitle}>
            Clear All User Sessions
            <span style={{ ...styles.badge, background: '#ffebee', color: '#c62828' }}>
              Destructive
            </span>
          </div>
          <div style={styles.cardDescription}>
            Permanently deletes every <code>exam_sessions</code> row tagged{' '}
            <code>source='user'</code>. This wipes real learner history in
            the current environment and cannot be undone. Gated behind a
            type-DELETE confirmation.
          </div>
          <div style={styles.cardRow}>
            <button
              data-testid="data-tools-clear-users-button"
              style={styles.dangerButton}
              disabled={busy !== null}
              onClick={openNukeModal}
            >
              {busy === 'clearUsers' ? 'Clearing…' : 'Clear All User Sessions…'}
            </button>
          </div>
          {userClearStatus && (
            <div style={styles.toast(userClearStatus.variant)}>
              {userClearStatus.message}
            </div>
          )}
        </div>

        {/* ── Refresh from Prod (disabled / v2 placeholder) ───────────── */}
        <div style={styles.card(false)}>
          <div style={styles.cardTitle}>
            Refresh from Prod
            <span style={styles.badge}>Coming in v2</span>
          </div>
          <div style={styles.cardDescription}>
            One-click copy of the production question bank into the current
            (non-prod) environment. Today this runs as a one-shot Python
            script; v2 will expose it here with a safety check to prevent
            running against prod.
          </div>
          <div style={styles.cardRow}>
            <button style={styles.disabledButton} disabled>
              Refresh from Prod
            </button>
          </div>
        </div>
      </div>

      {/* ── Nuclear confirm modal ──────────────────────────────────────── */}
      {showNukeModal && (
        <div data-testid="data-tools-nuke-modal" style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalHeading}>Delete ALL user sessions?</h3>
            <p style={styles.modalBody}>
              This will permanently delete every real learner session in the
              current Supabase environment. Dashboard metrics will reset to
              zero. To confirm, type <strong>DELETE</strong> below.
            </p>
            <input
              data-testid="data-tools-nuke-input"
              style={styles.modalInput}
              type="text"
              value={nukeConfirmText}
              onChange={(e) => setNukeConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              autoFocus
            />
            <div style={styles.modalActions}>
              <button
                style={styles.modalCancel}
                onClick={() => { setShowNukeModal(false); setNukeConfirmText(''); }}
              >
                Cancel
              </button>
              <button
                data-testid="data-tools-nuke-confirm"
                style={nukeConfirmText === 'DELETE' ? styles.dangerButton : styles.disabledButton}
                disabled={nukeConfirmText !== 'DELETE'}
                onClick={handleConfirmNuke}
              >
                Delete All User Sessions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
